import { css } from '@emotion/react';
import styled from '@emotion/styled';
import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';
import { cloneElement, forwardRef } from 'react';
import { border, compose, space } from 'styled-system';

import Icon from '../Icon';
import Text from '../Text';
import StyledInputField from './StyledInputField';

const StyledInputContainer = styled('div')`
  ${({ theme, full }) => css`
    color: ${theme.colors.input.text};
    display: inline-block;
    font-size: ${theme.typography.texts.size2.fontSize};
    line-height: ${theme.typography.texts.size2.lineHeight};
    position: relative;
    width: ${full && '100%'};

    ${theme.mediaQueries.lg} {
      font-size: ${theme.typography.texts.size1.fontSize};
      line-height: ${theme.typography.texts.size1.lineHeight};
    }
  `}
  ${compose(border, space)}
`;

const StyledInputWrapper = styled('div')`
  ${css`
    position: relative;
  `}
`;

const StyledHelperLink = styled('div')`
  ${({ theme }) => css`
    position: absolute;
    top: ${theme.space[1]};
    right: 0;
    cursor: pointer;
  `}
`;

const StyledInputLabel = styled(Text)`
  ${({ theme }) => css`
    display: inline-block;
    margin-bottom: ${theme.space[2]};
    user-select: none;
  `}
`;

const StyledInputMessage = styled('div')`
  ${({ theme, error }) => css`
    color: ${error ? theme.colors.input.error : theme.colors.input.message};
    display: block;
    margin-top: ${theme.space[1]};
    position: relative;
  `}
`;

const StyledIconWrapper = styled('div')`
  ${({ iconLeft, iconRight, theme }) => css`
    align-items: center;
    bottom: 0;
    display: flex;
    left: ${iconLeft && 0};
    padding: ${theme.space[3]} ${theme.space[2]};
    position: absolute;
    right: ${iconRight && 0};
    top: 0;
  `}
`;

const Input = forwardRef(
  (
    {
      autoComplete,
      defaultValue,
      disabled,
      error,
      full,
      helperLink,
      iconLeft,
      iconRight,
      id,
      label,
      maxLength,
      message,
      minLength,
      name,
      placeholder,
      required,
      type,
      value,
      onBlur,
      onChange,
      onClick,
      onFocus,
      onInvalid,
      onKeyDown,
      onKeyUp,
      ...rest
    },
    ref
  ) => {
    return (
      <StyledInputContainer full={full} {...rest}>
        {label && (
          <StyledInputLabel as="label" htmlFor={label}>
            {required ? `${label}*` : label}
          </StyledInputLabel>
        )}
        {helperLink && <StyledHelperLink>{helperLink}</StyledHelperLink>}
        <StyledInputWrapper>
          {iconLeft && !error && (
            <StyledIconWrapper iconLeft>
              {cloneElement(iconLeft, { color: 'input.icon', size: 'size2' })}
            </StyledIconWrapper>
          )}
          <StyledInputField
            autoComplete={autoComplete}
            defaultValue={defaultValue || undefined}
            disabled={disabled}
            error={error}
            full={full}
            id={label || id || null}
            iconLeft={iconLeft && !error}
            iconRight={iconRight || error}
            name={name}
            maxLength={maxLength}
            minLength={minLength}
            ref={ref}
            value={value}
            placeholder={placeholder}
            type={type}
            onBlur={onBlur}
            onClick={onClick}
            onChange={onChange}
            onFocus={onFocus}
            onInvalid={onInvalid}
            onKeyDown={onKeyDown}
            onKeyUp={onKeyUp}
          />
          {(!!iconRight || error) && (
            <StyledIconWrapper iconRight>
              {error ? (
                <Icon name="error-circle" size="size2" color="input.error" />
              ) : (
                cloneElement(iconRight, { size: 'size2' })
              )}
            </StyledIconWrapper>
          )}
        </StyledInputWrapper>
        {message && (
          <StyledInputMessage size="size3" error={error}>
            {message}
          </StyledInputMessage>
        )}
      </StyledInputContainer>
    );
  }
);

Input.displayName = 'Input';

Input.defaultProps = {
  autoComplete: 'off',
  id: null,
  defaultValue: null,
  disabled: false,
  error: false,
  full: false,
  iconLeft: null,
  iconRight: null,
  label: null,
  name: null,
  message: null,
  maxLength: null,
  minLength: null,
  value: '',
  placeholder: 'Escreva aqui',
  required: false,
  type: 'text',
  onBlur: () => {},
  onChange: () => {},
  onClick: () => {},
  onFocus: () => {},
  onInvalid: () => {},
  onKeyDown: () => {},
  onKeyUp: () => {},
};

Input.propTypes = {
  /**
   * Receives any HTML attr `autocomplete` value
   *
   * See: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#Values
   */
  autoComplete: PropTypes.string,
  /**
   * Default value for uncontrolled component
   */
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Whether the input field is disabled
   */
  disabled: PropTypes.bool,
  /**
   * Whether the input should render a error state
   */
  error: PropTypes.bool,
  /**
   * Renders input as a full width
   */
  full: PropTypes.bool,
  /**
   * Renders an icon on the left side
   */
  iconLeft: PropTypes.shape(),
  /**
   * Custom identifier for the input
   */
  id: PropTypes.string,
  /**
   * Renders an icon on the right side
   */
  iconRight: PropTypes.shape(),
  /**
   * Text to be rendered as a label
   */
  label: PropTypes.string,
  /**
   * Receives HTML attr `name` value
   */
  name: PropTypes.string,
  /**
   * Support message to be rendered below the input field
   */
  message: PropTypes.string,
  /**
   * Maximum length of value
   */
  maxLength: PropTypes.number,
  /**
   * Minimum length of value
   */
  minLength: PropTypes.number,
  /**
   * Value of the input
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Text that appears in the form control when the it is empty
   */
  placeholder: PropTypes.string,
  /**
   * Whether input has to contain a value to be submittable
   */
  required: PropTypes.bool,
  /**
   * One of the following HTML types, not all HTML values are
   * valid for this component
   *
   * See: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/Input#%3Cinput%3E_types
   */
  type: PropTypes.oneOf([
    'email',
    'date',
    'email',
    'hidden',
    'month',
    'number',
    'password',
    'tel',
    'text',
    'url',
    'week',
    'search',
  ]),
  /**
   * Callback fn to be executed on `onKeyDown` event
   */
  onKeyDown: PropTypes.func,
  /**
   * Callback fn to be executed on `onKeyUp` event
   */
  onKeyUp: PropTypes.func,
  /**
   * Callback fn to be executed on `onFocus` event
   */
  onFocus: PropTypes.func,
  /**
   * Callback fn to be executed on `onBlur` event
   */
  onBlur: PropTypes.func,
  /**
   * Callback fn to be executed on `onClick` event
   */
  onClick: PropTypes.func,
  /**
   * Callback fn to be executed on `onChange` event
   */
  onChange: PropTypes.func,
  /**
   * Callback fn to be executed on `onInvalid` event
   */
  onInvalid: PropTypes.func,
  /**
   * Implements `styled-system` [border](https://styled-system.com/api/#border) utility
   */
  ...propTypes.border,
  /**
   * Implements `styled-system` [space](https://styled-system.com/api/#space) utility
   */
  ...propTypes.space,
};

export default Input;
